import { DraggableAttributes, DragOverlay } from "@dnd-kit/core";
import { useEffect, useState } from "react";

import { archie } from "@smart/bridge-images-dom";
import { defaultAIFillSettings, SectionNS } from "@smart/bridge-types-basic";
import {
  Button,
  EditableText,
  Modal,
  Switch,
  SortableList,
  SortableItem,
} from "@smart/itops-sb-design-system-dom";
import {
  getDuplicatedTitle,
  isNotNullOrUndefined,
  rankBetween,
  sortField,
} from "@smart/itops-utils-basic";

import { MovableSection } from "./movable-section";
import { baseLanguageKey, LanguageKey } from "../../../../types";
import {
  EditableSection,
  GqlForm,
  UpdateAIFillSettings,
  UpdatedSection,
} from "../../types";

type EditSectionsModalProps = {
  editSectionsModal: boolean;
  onClose: () => void;
  sections: EditableSection[];
  updateAllSections: (sections: UpdatedSection[]) => Promise<void>;
  aiFillSettings: GqlForm["aiFillSettings"];
  updateAIFillSettings: UpdateAIFillSettings;
  viewingLanguage: LanguageKey;
};

const EditSectionsModal = ({
  editSectionsModal,
  onClose,
  sections,
  updateAllSections,
  aiFillSettings,
  updateAIFillSettings,
  viewingLanguage,
}: EditSectionsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [updatedSections, setUpdatedSections] = useState<UpdatedSection[]>([]);
  const [activeId, setActiveId] = useState<string>();
  const [newSectionUri, setNewSectionUri] = useState<string>();
  const defaultTitle =
    aiFillSettings?.sectionTitle || defaultAIFillSettings.sectionTitle;
  const [editingTitle, setEditingTitle] = useState(defaultTitle);
  const [autofillToggle, setAutofillToggle] = useState(
    !!aiFillSettings?.allowAiFill,
  );

  const getTranslatedTitle = (s: EditableSection) => {
    const translatedSection =
      viewingLanguage !== baseLanguageKey
        ? s.values?.translations?.find((t) => t.language === viewingLanguage)
        : undefined;
    return translatedSection?.title || s.values?.title || "";
  };

  useEffect(() => {
    setEditingTitle(defaultTitle);
    setAutofillToggle(!!aiFillSettings?.allowAiFill);
    if (editSectionsModal) {
      setNewSectionUri(undefined);
      setUpdatedSections(
        sortField(
          sections.map((s) => ({
            uri: s.uri,
            title: getTranslatedTitle(s),
            order: s.values?.order || rankBetween({}),
            description: s.values?.description,
            links: s.values?.links,
            updated: !s.values,
            created: !s.values,
          })),
          { key: "order", dir: "asc" },
        ),
      );
    }
  }, [editSectionsModal]);

  const moveSection = (from: number, to: number) => {
    const afterIndex = from > to ? to - 1 : to;
    const newOrder = rankBetween({
      after: updatedSections[afterIndex]?.order,
      before: updatedSections[afterIndex + 1]?.order,
    });

    const newSections = [...updatedSections];
    newSections[from] = {
      ...newSections[from],
      order: newOrder,
      updated: true,
    };
    setUpdatedSections(sortField(newSections, { key: "order", dir: "asc" }));
  };
  const activeIndex = activeId
    ? updatedSections.findIndex((s) => s.uri === activeId)
    : undefined;
  const activeSection = isNotNullOrUndefined(activeIndex)
    ? updatedSections[activeIndex]
    : undefined;

  const handleCopySection = (idx: number) => {
    const duplicatedSectionUri = SectionNS.generateUri();
    setNewSectionUri(duplicatedSectionUri);

    setUpdatedSections((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;

      const after = prev[idx].order;
      const before = prev[idx + 1]?.order;
      const newOrder = rankBetween({ after, before });

      const newItem: UpdatedSection = {
        ...prev[idx],
        uri: duplicatedSectionUri,
        title: getDuplicatedTitle(
          prev[idx].title,
          updatedSections.filter((s) => !s.deleted).map((s) => s.title),
        ),
        order: newOrder,
        // If we're duplicating an already duplicated section, use the uri of the original section
        copySectionUri: prev[idx].copySectionUri || prev[idx].uri,
      };

      const next = [...prev];
      next.splice(idx + 1, 0, newItem);

      return sortField(next, { key: "order", dir: "asc" });
    });
  };

  return (
    <Modal
      loading={loading}
      header={{
        icon: "solidPenToSquare",
        iconBgColor: "bg-cyan-140",
        text: "Edit sections",
      }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            text: "Save sections",
            variant: "primaryDefault",
            onClick: async () => {
              setLoading(true);
              await updateAllSections(updatedSections);
              if (aiFillSettings?.allowAiFill) {
                await updateAIFillSettings({
                  sectionTitle: editingTitle,
                  allowAiFill: autofillToggle,
                });
              }
              setLoading(false);
              onClose();
            },
            disabled: loading,
          },
        ],
      }}
      open={editSectionsModal}
      onClose={() => {
        if (!loading) onClose();
      }}
    >
      <div>
        {aiFillSettings?.allowAiFill && (
          <div className="flex justify-between gap-small bg-neutral-50 rounded pl-[0.8rem] pr-[1.6rem] py-[1.1rem] mb-[0.8rem]">
            <div className="flex gap-small">
              <img className="w-[1.6rem]" src={archie} alt="Archie" />
              <EditableText
                text={editingTitle}
                onChange={setEditingTitle}
                onComplete={async () => {}}
                dataTestId="autofill-title"
                fontSize="ui"
                fontWeight="semibold"
              />
            </div>
            <Switch
              toggled={autofillToggle}
              disabled={loading}
              onClick={() => setAutofillToggle(!autofillToggle)}
              dataTestId="ai-fill-toggle-sections-modal"
            />
          </div>
        )}
        <SortableList
          itemKeys={updatedSections.map((s) => s.uri)}
          onDragStart={({ active }) => {
            setActiveId(active.id as string);
          }}
          onDragEnd={({ active, over }) => {
            setActiveId(undefined);
            const activeItemIndex = updatedSections.findIndex(
              (s) => s.uri === active.id,
            );
            const overIndex = updatedSections.findIndex(
              (s) => s.uri === over?.id,
            );
            if (overIndex >= 0 && activeItemIndex !== overIndex) {
              moveSection(activeItemIndex, overIndex);
            }
          }}
        >
          <div
            className="flex flex-col gap-small mb-[1.6rem]"
            data-testid="sortable-sections"
          >
            {updatedSections.map((s, index) => (
              <SortableItem
                key={s.uri}
                id={s.uri}
                disabled={updatedSections.length <= 1}
                hidden={s.deleted}
              >
                {({ dragHandleProps, isDragging }) => (
                  <MovableSection
                    index={index}
                    item={s}
                    dragHandleProps={dragHandleProps}
                    numOfSections={updatedSections.length}
                    moveUp={(idx: number) => moveSection(idx, idx - 1)}
                    moveDown={(idx: number) => moveSection(idx, idx + 1)}
                    setTitle={(idx: number, title: string) => {
                      const newSections = [...updatedSections];
                      newSections[idx] = {
                        ...newSections[idx],
                        title,
                        updated: true,
                      };
                      setUpdatedSections(newSections);
                    }}
                    setDeleted={(idx: number, deleted: boolean) => {
                      const newSections = [...updatedSections];
                      newSections[idx].deleted = deleted;
                      setUpdatedSections(newSections);
                    }}
                    newSectionUri={newSectionUri}
                    isDragging={isDragging}
                    copySection={handleCopySection}
                  />
                )}
              </SortableItem>
            ))}
          </div>
          <DragOverlay>
            {isNotNullOrUndefined(activeIndex) && activeSection ? (
              <div className="cursor-grab">
                <MovableSection
                  index={activeIndex}
                  item={activeSection}
                  dragHandleProps={{
                    attributes: {} as DraggableAttributes,
                    listeners: {},
                  }}
                  numOfSections={updatedSections.length}
                  moveUp={() => {}}
                  moveDown={() => {}}
                  setTitle={() => {}}
                  setDeleted={() => {}}
                  newSectionUri={undefined}
                  isDragging={false}
                  copySection={handleCopySection}
                />
              </div>
            ) : null}
          </DragOverlay>
        </SortableList>
        <Button
          text="Add section"
          leftIcon="regularPlus"
          variant="primarySubtle"
          onClick={() => {
            const createdSectionUri = SectionNS.generateUri();
            setNewSectionUri(createdSectionUri);
            setUpdatedSections([
              ...updatedSections,
              {
                uri: createdSectionUri,
                title: "",
                order: rankBetween({
                  after: updatedSections[updatedSections.length - 1].order,
                }),
                updated: true,
                created: true,
              },
            ]);
          }}
        />
      </div>
    </Modal>
  );
};

export { EditSectionsModal };
